import { PhotoSwipeLightbox } from 'vendor';

var lightbox = null;
var suppressDrawerClose = false;
var refreshScheduled = false;
var checkoutPlusOpLock = false;
var DEFAULT_CHECKOUT_PLUS_VARIANT_ID = 55284693696897;

function getDrawer() {
  return document.querySelector('#cart-drawer.cart-drawer--redesign');
}

function isLightboxOpen() {
  return Boolean(lightbox?.pswp?.isOpen);
}

function patchDrawerForLightbox(drawer) {
  if (!drawer || drawer.dataset.hkLightboxPatched === 'true') return;
  drawer.dataset.hkLightboxPatched = 'true';

  const originalHideForOutsideClickTarget = drawer.hideForOutsideClickTarget.bind(drawer);
  drawer.hideForOutsideClickTarget = function (target) {
    if (target?.closest?.('.pswp')) return false;
    return originalHideForOutsideClickTarget(target);
  };

  const originalHide = drawer.hide.bind(drawer);
  drawer.hide = function (...args) {
    if (suppressDrawerClose || isLightboxOpen()) {
      return Promise.resolve();
    }
    return originalHide(...args);
  };
}

function getLightbox() {
  if (lightbox) return lightbox;

  lightbox = new PhotoSwipeLightbox({
    pswpModule: () => import('photoswipe'),
    bgOpacity: 1,
    maxZoomLevel: 3,
    closeTitle: window.themeVariables?.strings?.closeGallery || 'Close',
    arrowPrev: false,
    arrowNext: false,
    counter: false,
    close: false,
    zoom: false
  });

  lightbox.on('beforeOpen', () => {
    suppressDrawerClose = true;
  });

  lightbox.on('close', () => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        suppressDrawerClose = false;
      });
    });
  });

  lightbox.on('uiRegister', () => {
    lightbox.pswp.ui.registerElement({
      name: 'close-button',
      className: 'circle-button circle-button--xl hover:animate-icon-block',
      ariaLabel: window.themeVariables?.strings?.closeGallery || 'Close',
      order: 2,
      isButton: true,
      html: '<svg aria-hidden="true" focusable="false" fill="none" width="16" class="icon" viewBox="0 0 16 16"><path d="m1 1 14 14M1 15 15 1" stroke="currentColor" stroke-width="1"/></svg>',
      onClick: (event, _element, pswp) => {
        event?.preventDefault?.();
        event?.stopPropagation?.();
        pswp.close();
      }
    });
  });

  lightbox.init();
  return lightbox;
}

function bindZoomLinks(drawer) {
  if (!drawer) return;

  drawer.querySelectorAll('.hk-cart-drawer__zoom').forEach((link) => {
    if (link.dataset.hkZoomBound === 'true') return;
    link.dataset.hkZoomBound = 'true';

    link.addEventListener('click', (event) => {
      event.preventDefault();

      const allLinks = Array.from(drawer.querySelectorAll('.hk-cart-drawer__zoom'));
      const index = allLinks.indexOf(link);
      const items = allLinks.map((anchor) => {
        const img = anchor.closest('.hk-cart-drawer__item-media-wrap')?.querySelector('img');
        return {
          src: anchor.href,
          width: parseInt(anchor.getAttribute('data-pswp-width'), 10) || 1600,
          height: parseInt(anchor.getAttribute('data-pswp-height'), 10) || 1600,
          alt: img?.alt || '',
          msrc: img?.currentSrc || img?.src || anchor.href
        };
      });

      getLightbox().loadAndOpen(index, items);
    });
  });
}

function hideLegacyCartUi(drawer) {
  if (!drawer) return;

  drawer.querySelectorAll('.button-group.customize, .line-item, p.freeshipping').forEach((node) => {
    node.style.display = 'none';
  });

  drawer.querySelectorAll('.route-div:not(.hk-cart-drawer__postco-mount)').forEach((node) => {
    node.style.display = 'none';
  });

  drawer.querySelectorAll('.postco-checkout-plus-flex-product, [class*="postco-checkout-plus"]').forEach((node) => {
    if (node.closest('.hk-cart-drawer__postco-mount')) return;
    node.style.display = 'none';
  });
}

function formatMoney(cents) {
  var format = window.themeVariables?.settings?.moneyFormat || '£{{amount}}';
  var amount = (cents / 100).toFixed(2);

  return format
    .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amount.replace('.', ','))
    .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, String(Math.round(cents / 100)))
    .replace(/\{\{\s*amount\s*\}\}/g, amount);
}

function getCheckoutPlusVariantId(drawer) {
  var toggle = drawer && drawer.querySelector('[data-hk-checkout-plus-toggle]');
  var block = drawer && drawer.querySelector('.hk-cart-drawer__checkout-block');
  var fromToggle = toggle && parseInt(toggle.dataset.hkCheckoutPlusVariantId, 10);
  var fromBlock = block && parseInt(block.dataset.hkCheckoutPlusVariantId, 10);

  if (Number.isFinite(fromToggle)) return fromToggle;
  if (Number.isFinite(fromBlock)) return fromBlock;
  return DEFAULT_CHECKOUT_PLUS_VARIANT_ID;
}

function findVariantLine(cart, variantId) {
  if (!cart?.items?.length) return null;

  var index = cart.items.findIndex(function (item) {
    return Number(item.variant_id) === Number(variantId) || Number(item.id) === Number(variantId);
  });

  if (index === -1) return null;

  return {
    line: index + 1,
    key: cart.items[index].key || null,
    item: cart.items[index]
  };
}

async function fetchCartJson() {
  var response = await fetch('/cart.js', {
    credentials: 'same-origin',
    headers: { Accept: 'application/json' }
  });

  if (!response.ok) throw new Error('Cart fetch failed');
  return response.json();
}

async function addCheckoutPlusVariant(variantId) {
  var response = await fetch('/cart/add.js', {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json'
    },
    body: JSON.stringify({ id: Number(variantId), quantity: 1 })
  });

  var json = await response.json().catch(function () {
    return null;
  });

  if (!response.ok) {
    throw new Error((json && (json.description || json.message)) || 'Could not add Checkout+');
  }

  return json;
}

async function removeCheckoutPlusVariant(cart, variantId) {
  var found = findVariantLine(cart, variantId);
  if (!found) return cart;

  var payload = found.key
    ? { id: found.key, quantity: 0 }
    : { line: found.line, quantity: 0 };

  var response = await fetch('/cart/change.js', {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json'
    },
    body: JSON.stringify(payload)
  });

  var json = await response.json().catch(function () {
    return null;
  });

  if (!response.ok) {
    throw new Error((json && (json.description || json.message)) || 'Could not remove Checkout+');
  }

  return json;
}

function waitForDrawerRefresh(timeoutMs) {
  return new Promise(function (resolve) {
    var done = false;
    var timer = window.setTimeout(function () {
      if (done) return;
      done = true;
      document.removeEventListener('cart-drawer:refreshed', onRefreshed, true);
      resolve(false);
    }, timeoutMs || 3000);

    function onRefreshed() {
      if (done) return;
      done = true;
      window.clearTimeout(timer);
      document.removeEventListener('cart-drawer:refreshed', onRefreshed, true);
      resolve(true);
    }

    document.addEventListener('cart-drawer:refreshed', onRefreshed, true);
  });
}

async function refreshCartDrawer() {
  document.dispatchEvent(new CustomEvent('cart:refresh'));
  await waitForDrawerRefresh(3000);
}

async function ensureCheckoutPlusInCart(drawer, shouldHave) {
  var variantId = getCheckoutPlusVariantId(drawer);
  if (!variantId) return;

  var cart = await fetchCartJson();
  var found = findVariantLine(cart, variantId);

  if (shouldHave && !found) {
    await addCheckoutPlusVariant(variantId);
    await refreshCartDrawer();
    return;
  }

  if (!shouldHave && found) {
    await removeCheckoutPlusVariant(cart, variantId);
    await refreshCartDrawer();
  }
}

function goToCheckout() {
  window.location.assign('/checkout');
}

function setCheckoutButtonLoading(button, isLoading) {
  if (!button) return;

  if (isLoading) {
    button.setAttribute('aria-busy', 'true');
    button.classList.add('is-loading');
    button.disabled = true;
  } else {
    button.removeAttribute('aria-busy');
    button.classList.remove('is-loading');
    button.disabled = false;
  }
}

function updateCheckoutPlusPrice(drawer) {
  if (!drawer) return;

  var priceEl = drawer.querySelector('[data-hk-checkout-plus-price]');
  var totalEl = drawer.querySelector('[data-hk-cart-total-cents]');
  var toggle = drawer.querySelector('[data-hk-checkout-plus-toggle]');
  if (!priceEl) return;

  var totalCents = parseInt(totalEl?.dataset.hkCartTotalCents, 10);

  if (!Number.isFinite(totalCents)) {
    totalCents = parseInt(priceEl.dataset.hkCartTotalCents, 10);
  }

  if (Number.isFinite(totalCents)) {
    priceEl.textContent = formatMoney(totalCents);
  }

  var option = drawer.querySelector('.hk-cart-drawer__checkout-plus-option');
  if (option) {
    option.classList.toggle('is-selected', Boolean(toggle && toggle.checked));
  }
}

function isCheckoutPlusSelected(drawer) {
  var toggle = drawer.querySelector('[data-hk-checkout-plus-toggle]');
  return Boolean(toggle && toggle.checked);
}

function bindCheckoutPlusToggle(drawer) {
  if (!drawer) return;

  var toggle = drawer.querySelector('[data-hk-checkout-plus-toggle]');
  if (toggle && toggle.dataset.hkToggleBound !== 'true') {
    toggle.dataset.hkToggleBound = 'true';
    toggle.addEventListener('change', async function () {
      if (toggle.dataset.hkSyncing === 'true' || checkoutPlusOpLock) return;

      checkoutPlusOpLock = true;
      var option = drawer.querySelector('.hk-cart-drawer__checkout-plus-option');
      if (option) option.classList.add('is-syncing');

      try {
        await ensureCheckoutPlusInCart(drawer, toggle.checked);
      } catch (error) {
        console.error('Checkout+ toggle failed', error);
        toggle.checked = !toggle.checked;
        updateCheckoutPlusPrice(drawer);
      } finally {
        if (option) option.classList.remove('is-syncing');
        checkoutPlusOpLock = false;
      }
    });
  }

  drawer.querySelectorAll('.hk-cart-drawer__checkout-plus-info .hk-cart-drawer__info-tip-btn').forEach(function (btn) {
    if (btn.dataset.hkShieldBound === 'true') return;
    btn.dataset.hkShieldBound = 'true';
    btn.addEventListener('click', function (event) {
      event.stopPropagation();
    });
  });
}

function bindCheckoutActions(drawer) {
  if (!drawer) return;

  var checkoutBtn = drawer.querySelector('[data-hk-checkout-btn]');
  if (checkoutBtn && checkoutBtn.dataset.hkCheckoutBound !== 'true') {
    checkoutBtn.dataset.hkCheckoutBound = 'true';
    checkoutBtn.addEventListener('click', function (event) {
      event.preventDefault();
      triggerCheckout(drawer, isCheckoutPlusSelected(drawer));
    });
  }

  var skipBtn = drawer.querySelector('[data-hk-skip-checkout]');
  if (skipBtn && skipBtn.dataset.hkCheckoutBound !== 'true') {
    skipBtn.dataset.hkCheckoutBound = 'true';
    skipBtn.addEventListener('click', function (event) {
      event.preventDefault();
      triggerCheckout(drawer, false);
    });
  }
}

function bindCheckoutPlus(drawer) {
  if (!drawer) return;
  bindCheckoutPlusToggle(drawer);
  bindCheckoutActions(drawer);
}

async function triggerCheckout(drawer, withCheckoutPlus) {
  if (!drawer || checkoutPlusOpLock) return;

  var checkoutBtn = drawer.querySelector('[data-hk-checkout-btn]');
  var toggle = drawer.querySelector('[data-hk-checkout-plus-toggle]');

  if (toggle && toggle.checked !== withCheckoutPlus) {
    toggle.dataset.hkSyncing = 'true';
    toggle.checked = withCheckoutPlus;
    delete toggle.dataset.hkSyncing;
    updateCheckoutPlusPrice(drawer);
  }

  checkoutPlusOpLock = true;
  setCheckoutButtonLoading(checkoutBtn, true);

  try {
    await ensureCheckoutPlusInCart(drawer, withCheckoutPlus);

    var cart = await fetchCartJson();
    var variantId = getCheckoutPlusVariantId(drawer);
    var hasProtection = Boolean(findVariantLine(cart, variantId));

    if (withCheckoutPlus && !hasProtection) {
      throw new Error('Checkout+ protection was not added to the cart');
    }

    if (!withCheckoutPlus && hasProtection) {
      await removeCheckoutPlusVariant(cart, variantId);
    }

    goToCheckout();
  } catch (error) {
    console.error('Checkout failed', error);
    setCheckoutButtonLoading(checkoutBtn, false);
    checkoutPlusOpLock = false;
  }
}

function bindInsuranceTooltip(drawer) {
  if (!drawer) return;

  drawer.querySelectorAll('.hk-cart-drawer__info-tip').forEach((tip) => {
    if (tip.dataset.hkTipBound === 'true') return;
    tip.dataset.hkTipBound = 'true';

    const btn = tip.querySelector('.hk-cart-drawer__info-tip-btn');
    const panel = tip.querySelector('.hk-cart-drawer__info-tip-panel');
    if (!btn || !panel) return;

    btn.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      const isOpen = tip.classList.toggle('is-open');
      btn.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      panel.hidden = !isOpen;
    });
  });

  if (drawer.dataset.hkTipDismissBound === 'true') return;
  drawer.dataset.hkTipDismissBound = 'true';

  document.addEventListener('click', (event) => {
    if (event.target.closest('.hk-cart-drawer__info-tip')) return;

    drawer.querySelectorAll('.hk-cart-drawer__info-tip.is-open').forEach((tip) => {
      tip.classList.remove('is-open');
      const btn = tip.querySelector('.hk-cart-drawer__info-tip-btn');
      const panel = tip.querySelector('.hk-cart-drawer__info-tip-panel');
      if (btn) btn.setAttribute('aria-expanded', 'false');
      if (panel) panel.hidden = true;
    });
  });
}

function getDrawerScrollBody(drawer) {
  return drawer?.shadowRoot?.querySelector('[part="body"]') || null;
}

function restoreGiftPanelState(drawer) {
  const state = window.__hkGiftPanelRestore;
  if (!state?.keepOpen || !drawer) return;

  delete window.__hkGiftPanelRestore;

  const accordion = drawer.querySelector('#hk-gift-options');
  if (accordion) {
    accordion.open = true;
    accordion.dataset.hkPersistOpen = 'true';
  }

  const body = getDrawerScrollBody(drawer);
  if (!body) return;

  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      if (accordion) {
        const bodyRect = body.getBoundingClientRect();
        const accordionRect = accordion.getBoundingClientRect();
        const nextScrollTop = body.scrollTop + (accordionRect.top - bodyRect.top) - 12;
        body.scrollTop = Math.max(state.scrollTop, nextScrollTop);
      } else {
        body.scrollTop = state.scrollTop;
      }
    });
  });
}

function initAccordions(drawer) {
  if (!drawer) return;

  drawer.querySelectorAll('.hk-cart-drawer__accordion').forEach((accordion) => {
    if (accordion.dataset.hkPersistOpen === 'true') {
      accordion.open = true;
    }
  });
}

function scheduleRefreshDrawer(drawer) {
  if (refreshScheduled) return;
  refreshScheduled = true;

  requestAnimationFrame(function () {
    refreshScheduled = false;
    refreshDrawer(drawer);
  });
}

function refreshDrawer(drawer) {
  drawer = drawer || getDrawer();
  if (!drawer) return;

  patchDrawerForLightbox(drawer);
  hideLegacyCartUi(drawer);
  updateCheckoutPlusPrice(drawer);
  bindCheckoutPlus(drawer);
  bindInsuranceTooltip(drawer);
  initAccordions(drawer);
  bindZoomLinks(drawer);
  restoreGiftPanelState(drawer);
}

function initCartDrawerRedesign() {
  scheduleRefreshDrawer(getDrawer());
}

document.addEventListener('cart-drawer:refreshed', (event) => {
  const drawer = event.target?.tagName === 'CART-DRAWER' ? event.target : getDrawer();
  scheduleRefreshDrawer(drawer);
});

document.addEventListener('toggle', (event) => {
  const accordion = event.target;
  if (!accordion.classList?.contains('hk-cart-drawer__accordion')) return;
  if (accordion.open) {
    accordion.dataset.hkPersistOpen = 'true';
  } else {
    delete accordion.dataset.hkPersistOpen;
  }
}, true);

document.addEventListener('dialog:after-show', (event) => {
  if (event.target?.id === 'cart-drawer') {
    scheduleRefreshDrawer(event.target);
  }
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCartDrawerRedesign);
} else {
  initCartDrawerRedesign();
}
